import { useEffect, useCallback, useRef } from 'react'

export interface ShortcutConfig {
  /** Single key (e.g. 'k') OR a two-key sequence written as 'g d' (press g then d). */
  key: string
  ctrlKey?: boolean
  altKey?: boolean
  shiftKey?: boolean
  action: () => void
  description: string
  category: 'Navigation' | 'Actions' | 'General'
}

const IGNORED_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT'])

function isEditableTarget(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false
  if (IGNORED_TAGS.has(target.tagName)) return true
  if (target.isContentEditable) return true
  return false
}

/** True when the shortcut is a two-key "leader" sequence like `g d`. */
function isSequence(key: string): boolean {
  return key.includes(' ')
}

function matchesShortcut(e: KeyboardEvent, shortcut: ShortcutConfig): boolean {
  const keyMatch = (e.key ?? '').toLowerCase() === (shortcut.key ?? '').toLowerCase()
  const ctrlMatch = !!shortcut.ctrlKey === (e.ctrlKey || e.metaKey)
  const altMatch = !!shortcut.altKey === e.altKey
  const shiftMatch = !!shortcut.shiftKey === e.shiftKey
  return keyMatch && ctrlMatch && altMatch && shiftMatch
}

/**
 * Global keyboard-shortcut hook.
 *
 * Supports three flavours (all additive, none block typing in inputs):
 *  - modifier combos:   { key: 'k', ctrlKey: true }
 *  - single keys:       { key: '/' }, { key: 'n' }
 *  - leader sequences:  { key: 'g d' } — press `g` then `d` within 1s (Gmail-style)
 */
export function useKeyboardShortcuts(shortcuts: ShortcutConfig[]): void {
  const shortcutsRef = useRef(shortcuts)
  shortcutsRef.current = shortcuts

  // Pending leader key (e.g. after pressing `g`) + its expiry timer.
  const leaderRef = useRef<string | null>(null)
  const leaderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearLeader = useCallback(() => {
    leaderRef.current = null
    if (leaderTimerRef.current) {
      clearTimeout(leaderTimerRef.current)
      leaderTimerRef.current = null
    }
  }, [])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (isEditableTarget(e.target)) return

      const list = shortcutsRef.current
      const key = (e.key ?? '').toLowerCase()

      // ── Leader-sequence resolution ───────────────────────────────────────
      // If a leader (e.g. `g`) is pending, try to complete a `g x` sequence.
      if (leaderRef.current && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const combo = `${leaderRef.current} ${key}`
        const seq = list.find(
          (s) => isSequence(s.key) && (s.key ?? '').toLowerCase() === combo,
        )
        clearLeader()
        if (seq) {
          e.preventDefault()
          seq.action()
          return
        }
        // Not a valid completion — fall through so the key can still fire a
        // plain single-key shortcut.
      }

      // ── Begin a leader sequence ──────────────────────────────────────────
      // A bare key that is the first token of some `x y` sequence arms the leader.
      if (!e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
        const startsSequence = list.some(
          (s) => isSequence(s.key) && (s.key ?? '').toLowerCase().startsWith(`${key} `),
        )
        if (startsSequence) {
          e.preventDefault()
          leaderRef.current = key
          if (leaderTimerRef.current) clearTimeout(leaderTimerRef.current)
          leaderTimerRef.current = setTimeout(clearLeader, 1000)
          return
        }
      }

      // ── Plain combos / single keys ───────────────────────────────────────
      for (const shortcut of list) {
        if (isSequence(shortcut.key)) continue
        if (matchesShortcut(e, shortcut)) {
          e.preventDefault()
          e.stopPropagation()
          shortcut.action()
          return
        }
      }
    },
    [clearLeader],
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, true)
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true)
      if (leaderTimerRef.current) clearTimeout(leaderTimerRef.current)
    }
  }, [handleKeyDown])
}

/**
 * Default global shortcuts for the client portal.
 *
 * Two styles are offered so users can pick whichever they remember:
 *  - Gmail-style leader sequences (`g d`, `g r`, …) — the modern, discoverable set
 *  - single/`n` action keys + `Ctrl/⌘ K` palette + `/` search
 *
 * `openCreate` opens the command palette pre-scoped to "create" actions (falls
 * back to just opening the palette).
 */
export function getDefaultShortcuts(
  navigate: (path: string) => void,
  setCommandPaletteOpen: (open: boolean) => void,
): ShortcutConfig[] {
  return [
    // ── General ──────────────────────────────────────────────────────────
    {
      key: 'k',
      ctrlKey: true,
      action: () => setCommandPaletteOpen(true),
      description: 'Palette de commandes (rechercher / naviguer / créer)',
      category: 'General',
    },
    {
      key: '/',
      action: () => {
        const el =
          document.querySelector<HTMLInputElement>('[data-search-input]') ||
          document.querySelector<HTMLInputElement>('header input[type="text"], header input:not([type])')
        if (el) el.focus()
        else setCommandPaletteOpen(true)
      },
      description: 'Rechercher',
      category: 'General',
    },
    {
      key: 'n',
      action: () => setCommandPaletteOpen(true),
      description: 'Nouveau / Créer…',
      category: 'Actions',
    },
    {
      key: 'c',
      action: () => setCommandPaletteOpen(true),
      description: 'Créer dans le contexte actif',
      category: 'Actions',
    },

    // ── Navigation — Gmail-style leader sequences (press g, then the key) ──
    {
      key: 'g d',
      action: () => navigate('/dashboard'),
      description: 'Aller au tableau de bord',
      category: 'Navigation',
    },
    {
      key: 'g h',
      action: () => navigate('/hub'),
      description: 'Aller au Hub',
      category: 'Navigation',
    },
    {
      key: 'g r',
      action: () => navigate('/risk-pro'),
      description: 'Aller à RiskPRO',
      category: 'Navigation',
    },
    {
      key: 'g g',
      action: () => navigate('/governance-pro'),
      description: 'Aller à GovernancePRO',
      category: 'Navigation',
    },
    {
      key: 'g c',
      action: () => navigate('/compliance-pro'),
      description: 'Aller à CompliancePRO',
      category: 'Navigation',
    },
    {
      key: 'g a',
      action: () => navigate('/audit-pro'),
      description: 'Aller à AuditPRO',
      category: 'Navigation',
    },
    {
      key: 'g s',
      action: () => navigate('/strategy-pro'),
      description: 'Aller à StrategyPRO',
      category: 'Navigation',
    },
    {
      key: 'g m',
      action: () => navigate('/maturity-pro'),
      description: 'Aller à MaturityPRO',
      category: 'Navigation',
    },
    {
      key: 'g i',
      action: () => navigate('/chat'),
      description: 'Ouvrir l’assistant IA',
      category: 'Navigation',
    },
    // ── Extended per-module leaders (design audit R7 — full coverage) ──────
    {
      key: 'g v',
      action: () => navigate('/vendor-pro'),
      description: 'Aller à VendorPRO (tiers)',
      category: 'Navigation',
    },
    {
      key: 'g o',
      action: () => navigate('/data-pro'),
      description: 'Aller à DataPRO (données)',
      category: 'Navigation',
    },
    {
      key: 'g e',
      action: () => navigate('/ethics-pro'),
      description: 'Aller à EthicsPRO (éthique)',
      category: 'Navigation',
    },
    {
      key: 'g y',
      action: () => navigate('/security-pro'),
      description: 'Aller à SecurityPRO (cybersécurité)',
      category: 'Navigation',
    },
    {
      key: 'g t',
      action: () => navigate('/dashboard-pro'),
      description: 'Aller à DashboardPRO (tableaux de bord)',
      category: 'Navigation',
    },
    {
      key: 'g z',
      action: () => navigate('/ai-ia-pro'),
      description: 'Aller à AI-IA PRO (gouvernance IA)',
      category: 'Navigation',
    },
    {
      key: 'g b',
      action: () => navigate('/business-model-pro'),
      description: 'Aller à BusinessModelPRO',
      category: 'Navigation',
    },
    {
      key: 'g w',
      action: () => navigate('/workspace-pro'),
      description: 'Aller à WorkspacePRO (espace de travail)',
      category: 'Navigation',
    },
    {
      key: 'g p',
      action: () => navigate('/settings'),
      description: 'Ouvrir les paramètres',
      category: 'Navigation',
    },
  ]
}
